import type { AdminCentral, AdminCentralAdmin, AdminCentralPayload, Central, CentralStatus } from "@/types/admin";

const GRADIENTS = [
  "from-primary to-primary-2",
  "from-indigo to-primary",
  "from-mint to-green",
  "from-accent to-primary-2",
] as const;

type ApiCentralRow = {
  id?: number;
  name?: string;
  address?: string | null;
  status?: number;
  doctors_count?: number;
  country?: { name?: string } | null;
};

/** Maps a raw API central row to the legacy `Central` shape (used by detail + dashboard views). */
export function mapApiCentral(row: ApiCentralRow): Central {
  const name = String(row.name ?? "");
  const status: CentralStatus = row.status === 1 ? "active" : "expired";

  return {
    id: Number(row.id),
    name,
    location: String(row.address ?? row.country?.name ?? "—"),
    initial: name.slice(0, 1) || "—",
    gradient: GRADIENTS[Number(row.id ?? 0) % GRADIENTS.length],
    tier: "pro",
    status,
    doctors: Number(row.doctors_count ?? 0),
    sessions: 0,
    completion_rate: 0,
    renew: "—",
  };
}

/** Maps a raw API row to the rich AdminCentral shape for admin CRUD pages. */
export function mapApiAdminCentral(row: Record<string, unknown>): AdminCentral {
  return {
    id: Number(row.id),
    name: String(row.name ?? ""),
    license_number: (row.license_number as string | null) ?? null,
    status: Number(row.status ?? 0),
    status_label: row.status_label as string | undefined,
    address: (row.address as string | null) ?? null,
    mobile: (row.mobile as string | null) ?? null,
    avatar: (row.avatar as string | null) ?? null,
    country: (row.country as AdminCentral["country"]) ?? null,
    doctors_count: row.doctors_count !== undefined ? Number(row.doctors_count) : undefined,
    admins_count: row.admins_count !== undefined ? Number(row.admins_count) : undefined,
    admins: (row.admins as AdminCentralAdmin[] | undefined) ?? undefined,
    subscription:
      (row.subscription as AdminCentral["subscription"] | undefined | null) ?? null,
    created_at: (row.created_at as string | null) ?? null,
  };
}

export function centralFormToApi(payload: AdminCentralPayload): Record<string, unknown> {
  const out: Record<string, unknown> = { name: payload.name };
  if (payload.mobile !== undefined) out.mobile = payload.mobile;
  if (payload.address !== undefined) out.address = payload.address;
  if (payload.license_number !== undefined) out.license_number = payload.license_number;
  if (payload.country_id !== undefined) out.country_id = payload.country_id;
  if (payload.status !== undefined) out.status = payload.status;
  return out;
}

export function buildAdminCentralsQuery(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  name?: string;
  mobile?: string;
  license_number?: string;
  subscription_status?: string;
}): string {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.per_page) q.set("per_page", String(params.per_page));
  if (params?.search) q.set("filter[search]", params.search);
  if (params?.name) q.set("filter[name]", params.name);
  if (params?.mobile) q.set("filter[mobile]", params.mobile);
  if (params?.license_number) q.set("filter[license_number]", params.license_number);
  if (params?.subscription_status) q.set("filter[subscription_status]", params.subscription_status);
  const s = q.toString();
  return s ? `?${s}` : "";
}
